import { QueryClientProvider } from '@tanstack/react-query'
import { Redirect, Route, Switch, useParams, useSearch } from 'wouter'
import { useQuery } from '@tanstack/react-query'
import type { DocumentResponse } from '@server/sql/documents.ts'
import { api, keys } from './lib/api.ts'
import { queryClient } from './lib/api.ts'
import { I18nProvider } from './i18n/index.tsx'
import { ThemeProvider, DensityProvider } from './theme/provider.tsx'
import { Header } from './components/header.tsx'
import { ExploreScreen } from './routes/explore.tsx'
import { ResultsScreen } from './routes/results.tsx'
import { CompanyScreen } from './routes/company.tsx'
import { DocumentScreen } from './routes/document.tsx'
import { AboutScreen } from './routes/about.tsx'
import { GapsScreen } from './routes/gaps.tsx'
import { CorpusGapsScreen } from './routes/corpus-gaps.tsx'
import { AdminScreen } from './routes/admin.tsx'
import { LoginScreen } from './routes/login.tsx'
import { AuthProvider, useAuth } from './lib/auth.tsx'
import { NotFoundScreen } from './routes/not-found.tsx'

/**
 * Six routes (§10). Every screen's state lives in the URL -- the filter
 * selection is query params, the viewer's page is a param, the open tab is a
 * param. A person comparing two extractions will want two tabs open, and
 * will want to send someone a link to page 14 of document 41.
 */
function Routes() {
  return (
    <Switch>
      {/*
        The grid is the front door. Opening the app on an empty filter form
        asks a question before showing anything, and the answer for a corpus
        of thirteen companies is almost always "just show me them" -- so `/`
        lands on the companies and the filter is a place you go when the grid
        is too big, not a toll gate in front of it.

        The filter form keeps its own path rather than moving into a modal:
        its whole state is the query string, and a selection has to stay
        linkable.
      */}
      <Route path="/" component={HomeRedirect} />
      <Route path="/filter" component={ExploreScreen} />
      {/* Corpus-wide, and before /c/:symbol/gaps so it is not read as a symbol. */}
      <Route path="/gaps" component={CorpusGapsScreen} />
      <Route path="/results" component={ResultsScreen} />
      <Route path="/c/:symbol/d/:id" component={DocumentScreen} />
      {/* Before /c/:symbol, or wouter matches the company route first. */}
      <Route path="/c/:symbol/gaps" component={GapsScreen} />
      <Route path="/c/:symbol" component={CompanyScreen} />
      {/* A document reached directly still needs its company in the URL, so
          the header can offer a way back up. One redirect, one lookup. */}
      <Route path="/d/:id" component={DocumentRedirect} />
      <Route path="/admin" component={AdminScreen} />
      <Route path="/about" component={AboutScreen} />
      <Route component={NotFoundScreen} />
    </Switch>
  )
}

/**
 * `/` is the grid, and it keeps whatever it was asked with.
 *
 * The query string carries the whole filter selection, so a bookmarked
 * `/?sector=materials&period=q1` has to survive the hop -- dropping it would
 * silently widen a saved link to the entire corpus, which is the same class
 * of bug as a typo'd query parameter being ignored (§14).
 */
function HomeRedirect() {
  const search = useSearch()
  return <Redirect to={`/results${search ? `?${search}` : ''}`} replace />
}

/**
 * A document reached directly still needs its company in the URL, so the
 * header can offer a way back up. One lookup, then a real redirect -- rather
 * than a placeholder symbol that would make the resulting link unshareable.
 */
function DocumentRedirect() {
  const { id } = useParams<{ id: string }>()
  const search = useSearch()
  const documentId = Number(id)
  const { data, isError } = useQuery({
    queryKey: keys.document(documentId),
    queryFn: () => api<DocumentResponse>(`/api/documents/${documentId}`),
    enabled: Number.isFinite(documentId) && documentId > 0,
  })
  if (isError) return <NotFoundScreen />
  if (!data) return null
  return <Redirect to={`/c/${data.document.symbol}/d/${id}${search ? `?${search}` : ''}`} replace />
}

/**
 * Signed in, or the sign-in screen. Nothing in between.
 *
 * The whole app sits behind this rather than each screen checking: the server
 * refuses every /api and /media route without a session, so a shell rendered
 * for a signed-out visitor could only ever be a page of 401s.
 */
function Gate() {
  const { user, loading, setupRequired } = useAuth()

  // Nothing at all on the first request. A flash of the sign-in form for
  // someone who *is* signed in is worse than a blank moment.
  if (loading) return null
  if (!user || setupRequired || user.mustChangePassword) return <LoginScreen />

  /*
      The viewer needs the full viewport height and its own internal
      scrolling; every other screen scrolls its content. `dvh` rather
      than `vh` so a mobile URL bar does not clip the rail.

      `h-dvh` rather than `min-h-dvh`, and the scroll on <main>: with
      a *minimum* height the shell is still auto-height, so a column
      flex child has no definite height to resolve `flex: 1` against
      and every pane sized to its content instead. The viewer then
      grew past the viewport -- measured at 1720px inside a 900px
      window -- and its paging footer, the primary control on the
      screen, sat below the fold with no way to reach it but
      scrolling the whole app.

      A definite height here gives every descendant one, so `flex-1`
      and `min-h-0` do what they were written to do: the viewer fills
      the window exactly and scrolls inside its panes, while a long
      company page scrolls <main> under a header that stays put.
   */
  return (
      <div className="flex h-dvh flex-col overflow-hidden">
        <Header />
        <main className="flex min-h-0 flex-1 flex-col overflow-y-auto">
          <Routes />
        </main>
      </div>
  )
}

export function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <I18nProvider>
        <ThemeProvider>
          <DensityProvider>
            <AuthProvider>
              <Gate />
            </AuthProvider>
          </DensityProvider>
        </ThemeProvider>
      </I18nProvider>
    </QueryClientProvider>
  )
}
